// RoomBookings.js

import React, { useState, useEffect, useCallback } from "react";
import Paper from "@mui/material/Paper";
import Grid from "@mui/material/Grid";
import Chart from "./Chart";
import CompanyPieChart from "./CompanyPieChart";
import Deposits from "./Deposits";
import Title from "./Title";
import axios from "axios";
import CsvDownloadButton from "react-json-to-csv";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import DashboardTable from "./DashboardTable";
import Pagination from "@mui/material/Pagination";
import Box from "@mui/material/Box";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";

/**
 * Renders a component for displaying and managing room bookings data
 * Shows credits usage chart for the current month, total credits information,
 * company breakdown chart and table, and a paginated table of bookings with filtering options
 *
 * @returns {JSX.Element} - The rendered room bookings component
 */
function RoomBookings() {
  const [bookings, setBookings] = useState([]);
  const [totalCredits, setTotalCredits] = useState(0);
  const [creditsByDate, setCreditsByDate] = useState([]);
  const [creditsByCompany, setCreditsByCompany] = useState([]);
  const [companyBreakdown, setCompanyBreakdown] = useState([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalBookings, setTotalBookings] = useState(0);
  const [allBookings, setAllBookings] = useState([]); // Store all bookings for the month

  // Get current date for default values
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth(); // 0-11

  // Calculate 6 months from today
  const sixMonthsFromToday = new Date();
  sixMonthsFromToday.setMonth(sixMonthsFromToday.getMonth() + 6);
  const maxYear = sixMonthsFromToday.getFullYear();
  const maxMonth = sixMonthsFromToday.getMonth();

  // State for selected year and month
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);

  // Compute start and end dates based on selected year and month
  const [startDate, setStartDate] = useState(() => {
    // First day of current month
    return new Date(currentYear, currentMonth, 1);
  });

  const [endDate, setEndDate] = useState(() => {
    // Last day of current month
    return new Date(currentYear, currentMonth + 1, 0);
  });

  const [loading, setLoading] = useState(false);
  const [chartLoading, setChartLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // API endpoint
  const getEventsApi = process.env.REACT_APP_API_URL + "/api/get-events-list";

  // Generate arrays of available years and month names
  const years = Array.from({ length: maxYear - 2024 + 1 }, (_, i) => 2024 + i); // From 2024 to 6 months from today
  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  // Handler for year change
  const handleYearChange = (event) => {
    const newYear = event.target.value;
    setSelectedYear(newYear);

    // If selecting 2024 and current month is before March, set to March
    if (newYear === 2024 && selectedMonth < 2) {
      setSelectedMonth(2); // March
      updateDateRange(newYear, 2);
    } else if (newYear === maxYear && selectedMonth > maxMonth) {
      // If selecting the max year and current month is beyond the limit, set to max month
      setSelectedMonth(maxMonth);
      updateDateRange(newYear, maxMonth);
    } else {
      updateDateRange(newYear, selectedMonth);
    }
    // Reset to first page when changing year
    setPage(1);
    // Mark as needing to fetch all data again since date range changed
    setIsInitialLoad(true);
  };

  // Handler for month change
  const handleMonthChange = (event) => {
    const newMonth = event.target.value;

    // Prevent selecting months before March 2024
    if (selectedYear === 2024 && newMonth < 2) {
      return; // Don't allow selection
    }

    // Prevent selecting months beyond 6 months from today
    if (selectedYear === maxYear && newMonth > maxMonth) {
      return; // Don't allow selection
    }

    setSelectedMonth(newMonth);
    updateDateRange(selectedYear, newMonth);
    // Reset to first page when changing month
    setPage(1);
    // Mark as needing to fetch all data again since date range changed
    setIsInitialLoad(true);
  };

  // Update date range when year or month changes
  const updateDateRange = (year, month) => {
    // First day of selected month
    const newStartDate = new Date(year, month, 1);
    // Last day of selected month
    const newEndDate = new Date(year, month + 1, 0);

    setStartDate(newStartDate);
    setEndDate(newEndDate);
  };

  /**
   * Gets the JWT token from cookies
   * @returns {string|null} The JWT token or null if not found
   */
  const getAuthToken = useCallback(() => {
    const token = document.cookie
      .split("; ")
      .find((row) => row.startsWith("_auth="))
      ?.split("=")[1];

    if (!token) {
      throw new Error("Authentication token not found. Please log in again.");
    }

    return token;
  }, []);

  /**
   * Formats a Date using local calendar fields for API date filters.
   * Avoids UTC conversion shifts from toISOString() on month boundaries.
   */
  const formatDateForApi = useCallback((date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  }, []);

  /**
   * Processes bookings data to calculate chart data (credits by date)
   * @param {Array} bookingsData - Array of booking objects
   * @returns {Array} - Processed chart data
   */
  const processChartData = useCallback(
    (bookingsData) => {
      // Calculate total credits for the month
      const creditsSum = bookingsData.reduce(
        (sum, booking) => sum + (booking.credits || 0),
        0
      );
      setTotalCredits(creditsSum);

      // Process credits by date for chart - grouped by day
      const creditsByDay = {};

      // Initialize each day of the month with 0 credits
      const currentDate = new Date(startDate);
      const tempEndDate = new Date(endDate);

      // Ensure we have at least one day in the range to prevent infinite loops
      if (currentDate > tempEndDate) {
        tempEndDate.setDate(currentDate.getDate());
      }

      while (currentDate <= tempEndDate) {
        const dateKey = currentDate.toLocaleDateString();
        creditsByDay[dateKey] = 0;
        currentDate.setDate(currentDate.getDate() + 1);
      }

      // Add credits from bookings
      if (bookingsData && bookingsData.length > 0) {
        bookingsData.forEach((booking) => {
          if (booking && booking.start) {
            const date = new Date(booking.start).toLocaleDateString();
            creditsByDay[date] =
              (creditsByDay[date] || 0) + (booking.credits || 0);
          }
        });
      }

      // Convert to array format for chart
      const creditsByDateArray = Object.keys(creditsByDay).map((date) => ({
        date,
        credits: creditsByDay[date],
      }));

      // Sort by date
      creditsByDateArray.sort((a, b) => new Date(a.date) - new Date(b.date));

      return creditsByDateArray;
    },
    [startDate, endDate]
  );

  /**
   * Processes bookings data to calculate company breakdown for chart and table
   * @param {Array} bookingsData - Array of booking objects
   * @returns {Object} - Object containing chart data and table data
   */
  const processCompanyData = useCallback((bookingsData) => {
    // Group credits by company
    const creditsByCompanyMap = {};

    if (bookingsData && bookingsData.length > 0) {
      bookingsData.forEach((booking) => {
        if (booking && booking.companyName && booking.credits) {
          const companyName = booking.companyName || "Unknown Company";
          creditsByCompanyMap[companyName] =
            (creditsByCompanyMap[companyName] || 0) + (booking.credits || 0);
        }
      });
    }

    // Convert to array format for chart (sorted by credits descending)
    const chartData = Object.keys(creditsByCompanyMap)
      .map((company) => ({
        company,
        credits: creditsByCompanyMap[company],
      }))
      .sort((a, b) => b.credits - a.credits);

    // Calculate total credits for percentage calculation
    const totalCreditsAllCompanies = Object.values(creditsByCompanyMap).reduce(
      (sum, credits) => sum + credits,
      0
    );

    // Convert to array format for table with additional details
    const tableData = Object.keys(creditsByCompanyMap)
      .map((company) => {
        const companyBookings = bookingsData.filter(
          (booking) => (booking.companyName || "Unknown Company") === company
        );

        const totalBookings = companyBookings.length;
        const totalCredits = creditsByCompanyMap[company];
        const avgCreditsPerBooking =
          totalBookings > 0 ? (totalCredits / totalBookings).toFixed(2) : 0;

        return {
          companyName: company,
          totalBookings,
          totalCredits,
          avgCreditsPerBooking: parseFloat(avgCreditsPerBooking),
          percentage:
            totalCreditsAllCompanies > 0
              ? parseFloat(
                  ((totalCredits / totalCreditsAllCompanies) * 100).toFixed(1)
                )
              : 0,
        };
      })
      .sort((a, b) => b.totalCredits - a.totalCredits);

    return { chartData, tableData };
  }, []);

  /**
   * Format bookings for display in the table
   * @param {Array} bookingsData - Raw bookings data from API
   * @returns {Array} - Formatted bookings data
   */
  const formatBookingsForDisplay = useCallback((bookingsData) => {
    return bookingsData.map((booking) => ({
      ...booking,
      end: new Date(booking.end).toLocaleTimeString([], {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      }),
      start: new Date(booking.start).toLocaleTimeString([], {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      }),
      date: new Date(booking.start).toLocaleDateString(),
    }));
  }, []);

  /**
   * Creates empty chart data with dates in the selected range
   */
  const createEmptyChartData = useCallback(() => {
    const emptyChartData = [];
    const currentDate = new Date(startDate);
    const tempEndDate = new Date(endDate);

    // Ensure we have at least one day in the range
    if (currentDate > tempEndDate) {
      tempEndDate.setDate(currentDate.getDate());
    }

    while (currentDate <= tempEndDate) {
      const dateKey = currentDate.toLocaleDateString();
      emptyChartData.push({
        date: dateKey,
        credits: 0,
      });
      currentDate.setDate(currentDate.getDate() + 1);
    }

    return emptyChartData;
  }, [startDate, endDate]);

  /**
   * Initial data fetch - gets all data for the month and sets up pagination
   */
  const fetchInitialData = useCallback(async () => {
    if (loading || chartLoading) return; // Prevent multiple simultaneous calls

    setLoading(true);
    setChartLoading(true);
    setError(null);

    try {
      // Use the JWT token from cookies
      const token = getAuthToken();

      // Format dates for API
      const formattedStartDate = formatDateForApi(startDate);
      const formattedEndDate = formatDateForApi(endDate);

      // Make a single API call with a high limit to get all events for the month
      const response = await axios.get(getEventsApi, {
        params: {
          allEvents: true,
          page: 1,
          limit: 1000, // High limit to get all events for the month
          startDate: formattedStartDate,
          endDate: formattedEndDate,
        },
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        withCredentials: true,
      });

      // Check if response has the expected structure
      if (!response.data || !response.data.data) {
        throw new Error("Unexpected API response format");
      }

      // Store all bookings for the month
      const allBookingsData = response.data.data;
      setAllBookings(allBookingsData);

      // Calculate total bookings and pages
      const total = allBookingsData.length;
      setTotalBookings(total);
      setTotalPages(Math.ceil(total / limit) || 1);

      // Process chart data
      const chartData = processChartData(allBookingsData);
      setCreditsByDate(chartData);

      // Process company data
      const companyData = processCompanyData(allBookingsData);
      setCreditsByCompany(companyData.chartData);
      setCompanyBreakdown(companyData.tableData);

      // Get paginated data for the current page
      const startIndex = (page - 1) * limit;
      const endIndex = Math.min(startIndex + limit, allBookingsData.length);
      const paginatedBookings = allBookingsData.slice(startIndex, endIndex);

      // Format bookings for display
      const formattedBookings = formatBookingsForDisplay(paginatedBookings);
      setBookings(formattedBookings);
    } catch (error) {
      console.error("Error fetching data:", error);

      // Set user-friendly error message
      if (error.response && error.response.status === 401) {
        setError("Your session has expired. Please log in again.");
      } else if (error.message) {
        setError(`Error: ${error.message}`);
      } else {
        setError("Failed to load data. Please try again later.");
      }

      // Initialize empty data arrays
      setAllBookings([]);
      setBookings([]);
      setTotalPages(0);
      setTotalBookings(0);

      // Initialize empty chart data
      setCreditsByDate(createEmptyChartData());
      setCreditsByCompany([]);
      setCompanyBreakdown([]);
      setTotalCredits(0);
    } finally {
      setLoading(false);
      setChartLoading(false);
      setIsInitialLoad(false);
    }
  }, [
    startDate,
    endDate,
    getEventsApi,
    getAuthToken,
    limit,
    page,
    processChartData,
    processCompanyData,
    formatBookingsForDisplay,
    formatDateForApi,
    loading,
    chartLoading,
    createEmptyChartData,
  ]);

  /**
   * Updates pagination without making a new API call (uses existing data)
   */
  const updatePagination = useCallback(() => {
    if (allBookings.length === 0) return;

    const startIndex = (page - 1) * limit;
    const endIndex = Math.min(startIndex + limit, allBookings.length);
    const paginatedBookings = allBookings.slice(startIndex, endIndex);

    // Format bookings for display
    const formattedBookings = formatBookingsForDisplay(paginatedBookings);
    setBookings(formattedBookings);

    // Update pagination info
    setTotalPages(Math.ceil(allBookings.length / limit) || 1);
  }, [allBookings, page, limit, formatBookingsForDisplay]);

  // Effect to fetch initial data when date range changes
  useEffect(() => {
    if (isInitialLoad) {
      fetchInitialData();
    }
  }, [fetchInitialData, isInitialLoad]);

  // Effect to update pagination when page or limit changes (without making API calls)
  useEffect(() => {
    if (!isInitialLoad) {
      updatePagination();
    }
  }, [page, limit, updatePagination, isInitialLoad]);

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const handleLimitChange = (event) => {
    setLimit(event.target.value);
    setPage(1); // Reset to first page when changing items per page
  };

  const columns = [
    "Date",
    "Start Time",
    "End Time",
    "Room",
    "Company Name",
    "Description",
    "Email",
    "Credits",
  ];

  const columnMapping = {
    Date: "date",
    "Start Time": "start",
    "End Time": "end",
    Room: "roomName",
    "Company Name": "companyName",
    Description: "description",
    Email: "email",
    Credits: "credits",
  };

  // Company breakdown table configuration
  const companyColumns = [
    "Company Name",
    "Total Bookings",
    "Total Credits",
    "Avg Credits/Booking",
    "Percentage",
  ];

  const companyColumnMapping = {
    "Company Name": "companyName",
    "Total Bookings": "totalBookings",
    "Total Credits": "totalCredits",
    "Avg Credits/Booking": "avgCreditsPerBooking",
    Percentage: "percentage",
  };

  const companyFormatValues = {
    Percentage: (value) => `${value}%`,
  };

  if ((loading || chartLoading) && !bookings.length) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Grid
      container
      spacing={3}
    >
      {error && (
        <Grid
          item
          xs={12}
        >
          <Alert severity='error'>{error}</Alert>
        </Grid>
      )}

      {/* Date Filters - Top of Page */}
      <Grid
        item
        xs={12}
      >
        <Paper sx={{ p: 2, display: "flex", flexDirection: "column" }}>
          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            <FormControl sx={{ minWidth: 120 }}>
              <InputLabel id='year-select-label'>Year</InputLabel>
              <Select
                labelId='year-select-label'
                id='year-select'
                value={selectedYear}
                label='Year'
                onChange={handleYearChange}
                disabled={loading || chartLoading}
                size='small'
              >
                {years.map((year) => (
                  <MenuItem
                    key={year}
                    value={year}
                  >
                    {year}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl sx={{ minWidth: 150 }}>
              <InputLabel id='month-select-label'>Month</InputLabel>
              <Select
                labelId='month-select-label'
                id='month-select'
                value={selectedMonth}
                label='Month'
                onChange={handleMonthChange}
                disabled={loading || chartLoading}
                size='small'
              >
                {months.map((month, index) => (
                  <MenuItem
                    key={index}
                    value={index}
                    disabled={
                      (selectedYear === 2024 && index < 2) || // Disable Jan, Feb for 2024
                      (selectedYear === maxYear && index > maxMonth) // Disable months beyond 6 months from today
                    }
                  >
                    {month}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Paper>
      </Grid>

      {/* First Row: Bar Chart + Pie Chart + Deposits */}
      <Grid
        item
        xs={12}
        md={6}
        lg={6}
      >
        <Paper
          sx={{ p: 2, display: "flex", flexDirection: "column", height: 240 }}
        >
          <Title>
            Credits Used - {months[selectedMonth]} {selectedYear}
          </Title>
          {chartLoading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
              }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <Chart creditsByDate={creditsByDate} />
          )}
        </Paper>
      </Grid>

      <Grid
        item
        xs={12}
        md={3}
        lg={3}
      >
        <Paper
          sx={{ p: 2, display: "flex", flexDirection: "column", height: 240 }}
        >
          <Title>
            Credits by Company - {months[selectedMonth]} {selectedYear}
          </Title>
          {chartLoading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
              }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <CompanyPieChart creditsByCompany={creditsByCompany} />
          )}
        </Paper>
      </Grid>

      <Grid
        item
        xs={12}
        md={3}
        lg={3}
      >
        <Paper
          sx={{ p: 2, display: "flex", flexDirection: "column", height: 240 }}
        >
          <Deposits
            totalCredits={totalCredits}
            minDate={startDate}
            maxDate={endDate}
          />
        </Paper>
      </Grid>

      {/* Company Breakdown Table - Full Width */}
      <Grid
        item
        xs={12}
      >
        <Paper
          sx={{
            p: 2,
            display: "flex",
            flexDirection: "column",
            minHeight: 400,
            overflow: "hidden",
          }}
        >
          <Title>
            Company Breakdown
            <CsvDownloadButton
              data={companyBreakdown}
              filename={"Company Breakdown.csv"}
              style={{
                background: "none",
                border: "none",
                padding: "0px 0px 0px 10px",
                float: "right",
              }}
            >
              <FileDownloadOutlinedIcon />
            </CsvDownloadButton>
          </Title>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
              <CircularProgress />
            </Box>
          ) : (
            <DashboardTable
              data={companyBreakdown}
              columns={companyColumns}
              columnMapping={companyColumnMapping}
              formatValues={companyFormatValues}
            />
          )}
        </Paper>
      </Grid>

      {/* Main Bookings Table with Integrated Rows Per Page Filter */}
      <Grid
        item
        xs={12}
      >
        <Paper
          sx={{
            p: 2,
            display: "flex",
            flexDirection: "column",
            width: "100%",
            overflow: "hidden",
          }}
        >
          <React.Fragment>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Title>Room Bookings ({totalBookings} total)</Title>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <CsvDownloadButton
                  data={allBookings.map((booking) => ({
                    ...booking,
                    date: new Date(booking.start).toLocaleDateString(),
                    startTime: new Date(booking.start).toLocaleTimeString(),
                    endTime: new Date(booking.end).toLocaleTimeString(),
                  }))}
                  filename={"Booking List.csv"}
                  style={{
                    background: "none",
                    border: "none",
                    padding: "0px",
                  }}
                >
                  <FileDownloadOutlinedIcon />
                </CsvDownloadButton>
                <FormControl sx={{ width: 120 }}>
                  <InputLabel id='rows-per-page-label'>
                    Rows per page
                  </InputLabel>
                  <Select
                    labelId='rows-per-page-label'
                    id='rows-per-page'
                    value={limit}
                    label='Rows per page'
                    onChange={handleLimitChange}
                    disabled={loading}
                    size='small'
                  >
                    <MenuItem value={5}>5</MenuItem>
                    <MenuItem value={10}>10</MenuItem>
                    <MenuItem value={25}>25</MenuItem>
                    <MenuItem value={50}>50</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Box>
            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <DashboardTable
                  data={bookings}
                  columns={columns}
                  columnMapping={columnMapping}
                />
                {totalPages > 0 && (
                  <Box
                    sx={{ display: "flex", justifyContent: "center", mt: 2 }}
                  >
                    <Pagination
                      count={totalPages}
                      page={page}
                      onChange={handlePageChange}
                      color='primary'
                      disabled={loading}
                    />
                  </Box>
                )}
              </>
            )}
          </React.Fragment>
        </Paper>
      </Grid>
    </Grid>
  );
}

export default RoomBookings;
